import { useMemo } from 'react';
import { Box, Skeleton, Typography } from '@mui/material';
import { useLocalization } from '@/hooks/useLocalization';
import { useGetInvestorOpportunityTypesForDemoQuery } from '@/store/insightsApi';
import { formatNumberToCurrency } from '@/utils/currency';

type Props = {
  investorId: number;
  shouldFetch: boolean;
};

const InsightsCell: React.FC<Props> = ({ investorId, shouldFetch }) => {
  const { t } = useLocalization();
  const { data, isFetching } = useGetInvestorOpportunityTypesForDemoQuery(
    investorId,
    { skip: !shouldFetch },
  );

  const aggregated = useMemo(() => {
    const records = (data as any)?.Records ?? [];
    const filteredTypes = records.filter((t: any) => {
      const rules = Array.isArray(t?.Rules) ? t.Rules : [];
      const sumAmount = rules.reduce(
        (acc: number, r: any) => acc + (Number(r?.ExistingAmount ?? 0) || 0),
        0,
      );
      return sumAmount > 0;
    });
    let totalAmount = 0;
    let totalCount = 0;
    for (const type of filteredTypes) {
      const rules = Array.isArray(type?.Rules) ? type.Rules : [];
      for (const rule of rules) {
        const num = Number(rule?.ExistingNumber ?? 0) || 0;
        const amt = Number(rule?.ExistingAmount ?? 0) || 0;
        totalCount += num;
        totalAmount += amt;
      }
    }
    return { count: totalCount, amount: totalAmount };
  }, [data]);

  if (!shouldFetch) return null;
  if (isFetching) {
    return (
      <Box sx={{ width: '100%' }}>
        <Skeleton
          variant='text'
          width={100}
          height={18}
        />
        <Skeleton
          variant='text'
          width={80}
          height={14}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2 }}>
      <Typography
        variant='body2'
        sx={{ fontWeight: 600 }}
      >
        {t('activeInsights', {
          defaultValue: '{{count}} active insights',
          count: aggregated.count ?? 0,
        })}
      </Typography>
      <Typography
        variant='caption'
        color='text.secondary'
      >
        {formatNumberToCurrency(aggregated.amount ?? 0)}
      </Typography>
    </Box>
  );
};

export default InsightsCell;
